import {
  Alert,
  Box,
  Collapse,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { api } from '../../../../shared/services/api';
import type { IResponseAjud } from '../../../../shared/services/types/response';
import {
  StyledTableCellBody,
  StyledTableCellHead,
} from '../styledTableCell';
import type { IExecutionLog } from '../../types';

const DetailsContainer = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  margin: theme.spacing(1, 0),
  backgroundColor: theme.palette.background.default,
}));

interface AjudExecutionLogDetailsProps {
  open: boolean;
  row: IResponseAjud;
}

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const parseCurrency = (raw: string | number | null | undefined): number => {
  if (raw == null || raw === '') return 0;
  const str = String(raw);
  const normalized = str.includes(',')
    ? str.replace(/\./g, '').replace(',', '.')
    : str;
  const val = Number(normalized);
  return isNaN(val) ? 0 : val;
};
const AjudExecutionLogDetails = ({ open, row }: AjudExecutionLogDetailsProps) => {
  const token = Cookies.get('@token');
  const [logs, setLogs] = useState<IExecutionLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
    if (!open) {
      setLogs([]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/ajudArchive/executionLogs/${row.id}`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      setLogs(Array.isArray(response.data) ? response.data : []);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.message || 'Erro ao buscar logs');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const totalBlocked = parseCurrency(row.lock_value);
  const valueTransferred = parseCurrency(row.value_transference);
  const balanceToReturn = totalBlocked - valueTransferred;

  // Flag DESBLOQUEIO_SALDO_REMANESCENTE: type_record '05' (desbloqueio)
  // e unlock_remaining === '1'
  const isUnlockRemaining = row.unlock_remaining === '1';

  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <DetailsContainer elevation={0}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', mb: 1.5, color: 'text.secondary' }}
        >
          Rastro Financeiro — {row.court_order}
        </Typography>

        {isUnlockRemaining && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Desbloqueio de Saldo Remanescente:</strong>{' '}
              {formatCurrency(totalBlocked)} (Total Bloqueado)
              {' — '}
              {formatCurrency(valueTransferred)} (Valor Transferido)
              {' = '}
              <strong>{formatCurrency(balanceToReturn)}</strong> (Saldo a Devolver)
            </Typography>
          </Alert>
        )}

        {isLoading ? (
          <Typography variant="body2" sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            Carregando logs...
          </Typography>
        ) : logs.length === 0 ? (
          <Typography variant="body2" sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            Nenhum log de execução encontrado para este registro.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCellHead>Agência</StyledTableCellHead>
                <StyledTableCellHead>Conta</StyledTableCellHead>
                <StyledTableCellHead>Dígito</StyledTableCellHead>
                <StyledTableCellHead align="right">Valor Bloqueado</StyledTableCellHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, idx) => (
                <TableRow key={`${log.legacy_protocol_id}-${idx}`}>
                  <StyledTableCellBody>{log.agency || '-'}</StyledTableCellBody>
                  <StyledTableCellBody>{log.account || '-'}</StyledTableCellBody>
                  <StyledTableCellBody>{log.digit || '-'}</StyledTableCellBody>
                  <StyledTableCellBody align="right">
                    {log.value_blocked != null ? formatCurrency(parseCurrency(log.value_blocked)) : '-'}
                  </StyledTableCellBody>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Resumo financeiro */}
        {logs.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 3,
              mt: 1.5,
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Total bloqueado (logs):{' '}
              <strong>
                {formatCurrency(logs.reduce((sum, l) => sum + parseCurrency(l.value_blocked), 0))}
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Valor bloqueio (ordem):{' '}
              <strong>{formatCurrency(totalBlocked)}</strong>
            </Typography>
          </Box>
        )}
      </DetailsContainer>
    </Collapse>
  );
};

export default AjudExecutionLogDetails;
